/* ==========================================================================
   FILE: modules/mind/index.js
   IAMONEAI - Mind Module Index
   Re-exports all mind/memory/goals components
   ========================================================================== */

// ==========================================================================
// MAIN COMPONENTS
// ==========================================================================

export { 
  default as MemoryList,
  MemoryItem,
  MEMORY_TYPES,
  EPISTEMIC_TYPES 
} from './components/MemoryList';

export { 
  default as GoalList,
  GoalItem,
  GoalDetailModal,
  GOAL_STATUSES,
  GOAL_CATEGORIES 
} from './components/GoalList';

export { default as AnalyticsPanel } from './components/AnalyticsPanel';

// ==========================================================================
// CONSTANTS
// ==========================================================================

export const MIND_TABS = {
  MEMORIES: 'memories',
  GOALS: 'goals',
  ANALYTICS: 'analytics',
};

export const MEMORY_SOURCE_TYPES = {
  CHAT: 'chat',
  SYSTEM: 'system',
  OBSERVATION: 'observation',
  IDENTITY_SETUP: 'identity_setup',
  AUTO_DETECTED: 'auto_detected',
};

export const GOAL_CHECK_IN_FREQUENCIES = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  BIWEEKLY: 'biweekly',
  MONTHLY: 'monthly',
};

// ==========================================================================
// HELPER FUNCTIONS
// ==========================================================================

/**
 * Filter memories by type
 */
export function filterMemoriesByType(memories, type) {
  if (!type || type === 'all') return memories;
  return memories.filter(m => m.type === type);
}

/**
 * Filter memories by context
 */
export function filterMemoriesByContext(memories, contextId) {
  if (!contextId || contextId === 'all') return memories;
  return memories.filter(m => m.context_id === contextId);
}

/**
 * Filter goals by status
 */
export function filterGoalsByStatus(goals, status) {
  if (!status || status === 'all') return goals;
  return goals.filter(g => g.goal_data?.status === status);
}

/**
 * Get active goals (pending, not completed/abandoned)
 */
export function getActiveGoals(goals) {
  return goals.filter(g => 
    g.goal_data?.status === 'pending' || 
    g.goal_data?.status === 'paused'
  );
}

/**
 * Get overdue goals
 */
export function getOverdueGoals(goals) {
  const now = new Date();
  return goals.filter(g => 
    g.goal_data?.status === 'pending' && 
    g.goal_data?.deadline && 
    new Date(g.goal_data.deadline) < now
  );
}

/**
 * Sort memories by importance
 */
export function sortMemoriesByImportance(memories) {
  return [...memories].sort((a, b) => 
    (b.importance_score || 0) - (a.importance_score || 0)
  );
}

/**
 * Sort memories by recency
 */
export function sortMemoriesByRecency(memories) {
  return [...memories].sort((a, b) => 
    new Date(b.created_at || 0) - new Date(a.created_at || 0)
  );
}

/**
 * Group memories by type
 */
export function groupMemoriesByType(memories) {
  return memories.reduce((acc, memory) => {
    const type = memory.type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(memory);
    return acc;
  }, {});
}

/**
 * Calculate memory statistics
 */
export function calculateMemoryStats(memories) {
  const byType = {};
  const byContext = {};
  const byEpistemic = {};

  memories.forEach(m => {
    // By type
    const type = m.type || 'other';
    byType[type] = (byType[type] || 0) + 1;

    // By context
    const context = m.context_id || 'default';
    byContext[context] = (byContext[context] || 0) + 1;

    // By epistemic type
    const epistemic = m.epistemic_type || 'unknown';
    byEpistemic[epistemic] = (byEpistemic[epistemic] || 0) + 1;
  });

  return {
    total: memories.length,
    byType,
    byContext,
    byEpistemic,
  };
}

/**
 * Calculate goal statistics
 */
export function calculateGoalStats(goals) {
  let pending = 0;
  let completed = 0;
  let paused = 0;
  let abandoned = 0;
  let overdue = 0;

  const now = new Date();

  goals.forEach(g => {
    const status = g.goal_data?.status;
    const deadline = g.goal_data?.deadline;

    switch (status) {
      case 'pending':
        pending++;
        if (deadline && new Date(deadline) < now) {
          overdue++;
        }
        break;
      case 'done':
        completed++;
        break;
      case 'paused':
        paused++;
        break;
      case 'abandoned':
        abandoned++;
        break;
    }
  });

  return {
    total: goals.length,
    pending,
    completed,
    paused,
    abandoned,
    overdue,
    completionRate: goals.length > 0 
      ? Math.round((completed / goals.length) * 100) 
      : 0,
  };
}